import { ComponentPropsWithoutRef, MouseEvent, useCallback } from 'react';
import theme from 'App.theme';
import { Box } from '@mui/material';

type Variant = 'tray' | 'turnaroundOrigin' | 'express' | 'turnaroundDestination' | 'selectedRate' | 'consolidated';

const colorMap: Record<Variant, { border: string; background: string; color: string; hoverBackground: string }> = {
  tray: {
    border: theme.colors.teal,
    background: theme.colors.tealLight,
    color: theme.colors.teal,
    hoverBackground: theme.colors.tealLight,
  },
  turnaroundOrigin: {
    border: theme.colors.primary,
    background: theme.colors.primaryLight,
    color: theme.colors.primary,
    hoverBackground: theme.colors.primaryTrans,
  },
  turnaroundDestination: {
    border: theme.colors.yellow,
    background: theme.colors.transYellow,
    color: theme.colors.yellow,
    hoverBackground: theme.colors.lightYellow2,
  },
  express: {
    border: theme.colors.red,
    background: theme.colors.white,
    color: theme.colors.red,
    hoverBackground: theme.colors.white,
  },
  selectedRate: {
    border: theme.colors.red,
    background: theme.colors.transOrange,
    color: theme.colors.red,
    hoverBackground: theme.colors.transOrange,
  },
  consolidated: {
    border: theme.colors.purple,
    background: theme.colors.transPurple,
    color: theme.colors.purple,
    hoverBackground: theme.colors.transPurple,
  },
};

type Props = {
  variant: Variant;
  disabled?: boolean;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  width?: string | number;
  height?: string | number;
  padding?: string;
} & ComponentPropsWithoutRef<'div'>;

export const DeliveryChip = ({
  variant,
  style,
  disabled,
  onClick,
  width = 'auto',
  height = 'auto',
  padding = `1px ${theme.layout.spacing.small}`,
  ...props
}: Props) => {
  const colors = colorMap[variant];

  const handleClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (disabled) {
        return;
      }

      // Prevent the click from propagating to the parent SetButton
      // This would be more appropriate in SetButton itself but that didn't work for some reason
      event.stopPropagation();

      if (onClick) {
        onClick(event);
      }
    },
    [disabled, onClick],
  );

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 1,
        width,
        height,
        minHeight: 15,
        border: `1px solid`,
        borderRadius: 3,
        padding,
        fontSize: 10,
        borderColor: colors.border,
        backgroundColor: colors.background,
        color: colors.color,
        cursor: onClick && !disabled ? 'pointer' : 'default',
        ...(onClick &&
          !disabled && {
            transition: 'background-color 0.3s',
            '&:hover': {
              backgroundColor: colors.hoverBackground,
            },
          }),
        ...style,
      }}
      {...props}
    />
  );
};
